import React, { useState } from 'react';
import { Search, Star, Plus, ChevronLeft } from 'lucide-react';
import { useClients } from '../../../contexts/ClientsContext';
import { ClientForm } from './ClientForm';
import { useNavigate } from 'react-router-dom';

export function ClientsScreen() {
  const navigate = useNavigate();
  const { clients, loading, error, addClient, updateClient } = useClients();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Filtrar y ordenar clientes
  const filteredClients = clients
    .filter(client => 
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleSubmit = async (formData) => {
    try {
      if (selectedClient) {
        await updateClient(selectedClient.id, formData);
      } else {
        await addClient(formData);
      }
      setShowForm(false);
      setSelectedClient(null);
    } catch (err) {
      console.error('Error saving client:', err);
      // TODO: Mostrar error al usuario
    }
  };

  const handleEditClick = (client) => {
    setSelectedClient(client);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-gray-500 dark:text-gray-400">Cargando clientes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-red-500 dark:text-red-400">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 z-10">
        <div className="p-4">
          <div className="flex items-center gap-4 mb-4">
            <button 
              onClick={() => navigate(-1)}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Clientes</h1>
          </div>
          
          {/* Buscador */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Lista de clientes */}
      <div className="p-4">
        <div className="space-y-3">
          {filteredClients.map(client => (
            <div
              key={client.id}
              onClick={() => handleEditClick(client)}
              className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {client.name}
                  </div>
                  {client.hasCurrentAccount && (
                    <div className={`text-sm ${
                      client.balance >= 0 
                        ? 'text-green-600 dark:text-green-400' 
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                      Saldo: ${client.balance.toLocaleString()}
                    </div>
                  )}
                </div>
                {client.hasCurrentAccount && (
                  <Star className="w-5 h-5 text-yellow-500 dark:text-yellow-400" />
                )}
              </div>
            </div>
          ))}

          {filteredClients.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No se encontraron clientes
            </div>
          )}
        </div>
      </div>

      {/* Botón flotante para agregar cliente */}
      <button
        onClick={() => {
          setSelectedClient(null);
          setShowForm(true);
        }}
        className="fixed bottom-20 right-4 p-4 bg-blue-500 dark:bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Formulario modal */}
      {showForm && (
        <ClientForm
          client={selectedClient}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowForm(false);
            setSelectedClient(null);
          }}
        />
      )}
    </div>
  );
} 